import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import useSeo from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';
import { categoryBadgeClass, formatViews } from '@/utils/format';
import ArticleBody from '@/pages/article/components/ArticleBody';

export default function ArticlePage() {
  const { slug = '' } = useParams();
  const article = articles.find((item) => item.slug === slug);

  const headings = article
    ? article.content
        .map((line, index) =>
          line.startsWith('## ') ? { id: `section-${index}`, text: line.slice(3) } : null,
        )
        .filter((item): item is { id: string; text: string } => item !== null)
    : [];

  const sorted = [...articles].sort((a, b) => (a.date < b.date ? 1 : -1));
  const currentIndex = sorted.findIndex((item) => item.slug === slug);
  const prev = currentIndex > 0 ? sorted[currentIndex - 1] : null;
  const next = currentIndex >= 0 && currentIndex < sorted.length - 1 ? sorted[currentIndex + 1] : null;

  const related = article
    ? articles.filter((item) => item.category === article.category && item.slug !== article.slug).slice(0, 3)
    : [];

  useSeo({
    title: article ? `${article.title} | 美日吃瓜` : '文章不存在 | 美日吃瓜',
    description: article ? article.summary.slice(0, 150) : '这篇观察暂时找不到，返回美日吃瓜首页继续围观。',
    keywords: article ? `美日吃瓜,${article.categoryName},${article.tags.join(',')}` : '美日吃瓜',
    path: `/article/${slug}`,
    jsonLd: article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: article.title,
            description: article.summary,
            datePublished: article.date,
            author: { '@type': 'Organization', name: article.author },
            publisher: { '@type': 'Organization', name: '美日吃瓜' },
            inLanguage: 'zh-CN',
            keywords: article.tags.join(','),
            mainEntityOfPage: `https://hnqyjk.cn/article/${article.slug}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hnqyjk.cn/' },
              { '@type': 'ListItem', position: 2, name: '话题总览', item: 'https://hnqyjk.cn/topics' },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.categoryName,
                item: `https://hnqyjk.cn/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 4,
                name: article.title,
                item: `https://hnqyjk.cn/article/${article.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <i className="ri-file-unknow-line text-5xl text-foreground-400"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">这篇文章走丢了</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">链接可能已经失效，回到话题总览继续逛吧。</p>
          <Link
            to="/topics"
            className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回话题总览
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <article>
          <header className="border-b border-background-200 bg-background-100">
            <div className="mx-auto w-full max-w-5xl px-4 py-10 md:px-6 md:py-14">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '话题总览', to: '/topics' },
                  { label: article.categoryName, to: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
              <Link
                to={`/category/${article.category}`}
                className={`mt-6 inline-block rounded-full border px-3 py-1 text-xs font-medium transition-colors ${categoryBadgeClass(article.category)}`}
              >
                {article.categoryName}
              </Link>
              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl md:leading-tight">
                {article.title}
              </h1>
              <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {article.summary}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-user-3-line text-sm"></i>
                  </span>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读约 {article.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(article.views)} 次围观
                </span>
              </div>
            </div>
          </header>

          <div className="mx-auto w-full max-w-5xl px-4 py-10 md:px-6 md:py-14">
            <div className="h-64 w-full overflow-hidden rounded-lg md:h-[420px]">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 美日吃瓜`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-8">
                <ArticleBody content={article.content} />

                <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-background-200 pt-8">
                  <span className="text-sm font-medium text-foreground-700">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-background-300 bg-background-100 px-3 py-1 text-xs text-foreground-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <nav className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" aria-label="上下篇">
                  {prev ? (
                    <Link
                      to={`/article/${prev.slug}`}
                      className="group rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-400"
                    >
                      <span className="flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-arrow-left-line"></i>
                        上一篇
                      </span>
                      <span className="mt-2 block font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600">
                        {prev.title}
                      </span>
                    </Link>
                  ) : (
                    <span className="hidden sm:block"></span>
                  )}
                  {next && (
                    <Link
                      to={`/article/${next.slug}`}
                      className="group rounded-lg border border-background-200 bg-background-50 p-5 text-right transition-colors hover:border-primary-400"
                    >
                      <span className="flex items-center justify-end gap-1 text-xs text-foreground-500">
                        下一篇
                        <i className="ri-arrow-right-line"></i>
                      </span>
                      <span className="mt-2 block font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600">
                        {next.title}
                      </span>
                    </Link>
                  )}
                </nav>
              </div>

              <aside className="lg:col-span-4">
                <div className="sticky top-24 flex flex-col gap-6">
                  {headings.length > 0 && (
                    <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                      <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                        <i className="ri-list-check-2 text-primary-500"></i>
                        本文目录
                      </h2>
                      <ul className="mt-4 space-y-3">
                        {headings.map((item, index) => (
                          <li key={item.id}>
                            <a
                              href={`#${item.id}`}
                              className="flex items-start gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-600"
                            >
                              <span className="mt-0.5 font-heading text-xs font-bold text-primary-400">
                                {String(index + 1).padStart(2, '0')}
                              </span>
                              {item.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
                    <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                      <i className="ri-information-line text-secondary-600"></i>
                      阅读提示
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                      本文为文化现象观察与观点整理，不代表对任何个人或群体的定性评价。欢迎带着自己的判断来读。
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="border-t border-background-200 bg-background-100">
            <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-16">
              <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
                    <span className="inline-block h-px w-8 bg-primary-400"></span>
                    More
                  </p>
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                    同栏目相关推荐
                  </h2>
                </div>
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  进入「{article.categoryName}」
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <ArticleCard key={item.slug} article={item} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}